import React, { useState } from 'react';

export interface CourseIdea {
  id: string;
  title: string;
  description: string;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  targetAudience: string;
  estimatedDuration: string;
  topics: string[];
  votes: number;
  requests: number;
  hasUserVoted?: boolean;
  hasUserRequested?: boolean;
}

interface CourseIdeaCardProps {
  idea: CourseIdea;
  onVote: (ideaId: string, voteType: 'up' | 'down') => void;
  onRequest: (ideaId: string) => void;
}

const CourseIdeaCard: React.FC<CourseIdeaCardProps> = ({ idea, onVote, onRequest }) => {
  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Beginner':
        return 'bg-green-100 text-green-800';
      case 'Intermediate':
        return 'bg-yellow-100 text-yellow-800';
      case 'Advanced':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getDifficultyIcon = (difficulty: string) => {
    switch (difficulty) {
      case 'Beginner':
        return '🌱';
      case 'Intermediate':
        return '🌳';
      case 'Advanced':
        return '🚀';
      default:
        return '📊';
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-gray-100 hover:border-blue-200 transform hover:-translate-y-1">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
            {idea.title}
          </h3>
          <p className="text-gray-600 text-sm leading-relaxed mb-3">
            {idea.description}
          </p>
        </div>
        <div className={`px-3 py-1 rounded-full text-xs font-semibold ml-4 flex items-center gap-1 ${getDifficultyColor(idea.difficulty)}`}>
          <span>{getDifficultyIcon(idea.difficulty)}</span>
          {idea.difficulty}
        </div>
      </div>

      <div className="space-y-3 mb-4">
        <div className="flex items-center text-sm text-gray-600">
          <span className="mr-2">👥</span>
          <span className="font-medium">Target:</span>
          <span className="ml-1">{idea.targetAudience}</span>
        </div>
        
        <div className="flex items-center text-sm text-gray-600">
          <span className="mr-2">⏱️</span>
          <span className="font-medium">Duration:</span>
          <span className="ml-1">{idea.estimatedDuration}</span>
        </div>
        
        {idea.topics && idea.topics.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {idea.topics.map((topic, index) => (
              <span 
                key={index}
                className="bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded-full border border-blue-200"
              >
                {topic}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="border-t pt-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => onVote(idea.id, 'up')}
              className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                idea.hasUserVoted 
                  ? 'bg-blue-100 text-blue-700 border border-blue-200' 
                  : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50 border border-gray-200 hover:border-blue-200'
              }`}
            >
              <span className="text-lg">👍</span>
              <span>{idea.votes}</span>
            </button>
            
            <div className="flex items-center gap-1 text-sm text-gray-500">
              <span>🔥</span>
              <span>{idea.requests} requests</span>
            </div>
          </div>
          
          <button
            onClick={() => onRequest(idea.id)}
            className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-200 flex items-center gap-2 ${
              idea.hasUserRequested
                ? 'bg-green-100 text-green-700 border border-green-200'
                : 'bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700 shadow-md hover:shadow-lg'
            }`}
            disabled={idea.hasUserRequested}
          >
            {idea.hasUserRequested ? (
              <>
                <span>✅</span>
                <span>Requested</span>
              </>
            ) : (
              <>
                <span>🎯</span>
                <span>Request Course</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

interface CourseIdeaGeneratorProps {
  className?: string;
}

export const CourseIdeaGenerator: React.FC<CourseIdeaGeneratorProps> = ({ className = '' }) => {
  const [ideas, setIdeas] = useState<CourseIdea[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasGenerated, setHasGenerated] = useState(false);

  const generateCourseIdeas = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/course-ideas/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setIdeas(data.ideas);
        setHasGenerated(true);
      } else {
        setError(data.error || 'Failed to generate course ideas');
      }
    } catch (err) {
      setError('Network error occurred while generating ideas');
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (ideaId: string, voteType: 'up' | 'down') => {
    try {
      const response = await fetch(`/api/course-ideas/${ideaId}/vote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ voteType }),
      });
      
      if (response.ok) {
        setIdeas(ideas.map(idea => 
          idea.id === ideaId 
            ? { 
                ...idea, 
                votes: voteType === 'up' ? idea.votes + 1 : idea.votes - 1,
                hasUserVoted: voteType === 'up' ? true : false
              }
            : idea
        ));
      }
    } catch (error) {
      console.error('Error voting on idea:', error);
    }
  };

  const handleRequest = async (ideaId: string) => {
    try {
      const response = await fetch(`/api/course-ideas/${ideaId}/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        setIdeas(ideas.map(idea => 
          idea.id === ideaId 
            ? { 
                ...idea, 
                requests: idea.requests + 1,
                hasUserRequested: true
              }
            : idea
        ));
      }
    } catch (error) {
      console.error('Error requesting course:', error);
    }
  };

  return (
    <div className={`bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden ${className}`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 via-blue-600 to-indigo-700 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white mb-2 flex items-center gap-3">
              <span className="text-3xl">🎓</span>
              AI Course Idea Generator
            </h2>
            <p className="text-blue-100">
              Get personalized course suggestions powered by community insights
            </p>
          </div>
          <div className="text-4xl">✨</div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {!hasGenerated && (
          <div className="text-center py-8">
            <div className="text-6xl mb-4">🚀</div>
            <h3 className="text-xl font-semibold text-gray-800 mb-4">
              Discover Your Next Learning Adventure
            </h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Our AI will analyze community trends and generate personalized course ideas 
              tailored to current demands and emerging technologies.
            </p>
            
            <button
              onClick={generateCourseIdeas}
              disabled={loading}
              className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-3 mx-auto"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Generating Ideas...</span>
                </>
              ) : (
                <>
                  <span className="text-xl">🎯</span>
                  <span>Generate Course Ideas</span>
                </>
              )}
            </button>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2">
              <span className="text-red-500 text-xl">⚠️</span>
              <p className="text-red-700 font-medium">Error generating course ideas</p>
            </div>
            <p className="text-red-600 text-sm mt-1">{error}</p>
            <button
              onClick={generateCourseIdeas}
              className="mt-3 text-red-600 hover:text-red-800 text-sm font-medium underline"
            >
              Try Again
            </button>
          </div>
        )}

        {loading && hasGenerated && (
          <div className="flex justify-center items-center py-8">
            <div className="flex items-center gap-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              <span className="text-gray-600 font-medium">Generating fresh ideas...</span>
            </div>
          </div>
        )}

        {ideas.length > 0 && !loading && (
          <>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <span>💡</span>
                  Generated Course Ideas ({ideas.length})
                </h3>
                <p className="text-gray-600 text-sm">
                  Vote for your favorites and request courses you'd like to see created
                </p>
              </div>
              
              <button
                onClick={generateCourseIdeas}
                disabled={loading}
                className="text-purple-600 hover:text-purple-700 font-medium text-sm flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-purple-50 transition-colors"
              >
                <span>🔄</span>
                <span>Generate New</span>
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {ideas.map((idea) => (
                <CourseIdeaCard
                  key={idea.id}
                  idea={idea}
                  onVote={handleVote}
                  onRequest={handleRequest}
                />
              ))}
            </div>

            <div className="mt-8 text-center">
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4 border border-blue-100">
                <p className="text-sm text-gray-600">
                  <span className="font-semibold">💪 Community Power:</span> The most voted and requested ideas 
                  will be prioritized for course creation. Your voice matters!
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};